import { friendlyError } from "../../../lib/friendlyError";
import { trpc } from "../../../trpc";
import { TestThenSend } from "../TestThenSend";
import {
  EMAIL_OFF_SENTENCE,
  people,
  SEND_OFFLINE_SENTENCE,
  sendAllSentence,
} from "../sendCopy";
import { BODY_TEXT } from "../ui";
import { useSendFlow } from "../useSendFlow";

/**
 * The entry pass: one email per guest who booked, each with their own QR
 * code and reference number, sent ahead so the door can scan them in.
 */
export function EntryPassPanel({
  eventSlug,
  audienceCount,
  defaultTestEmail,
  senderName,
  alreadySentNote,
}: {
  eventSlug: string;
  /** Addresses that booked, not walk-ins. */
  audienceCount: number;
  defaultTestEmail: string;
  senderName: string | null;
  alreadySentNote: string | null;
}) {
  const flow = useSendFlow(defaultTestEmail);
  const utils = trpc.useUtils();
  const qrPass = trpc.adminSendEventQrPass.useMutation();

  const send = (mode: "test" | "all") => {
    qrPass.mutate(
      {
        eventSlug,
        mode,
        testEmail: mode === "test" ? flow.testAddress.trim() : undefined,
        testName: mode === "test" ? (senderName ?? undefined) : undefined,
      },
      {
        onSuccess: (res) => {
          if (res.mode === "test") {
            flow.testSent(
              res.skipped
                ? EMAIL_OFF_SENTENCE
                : `Test sent to ${res.sentTo}. Open it on your phone and check the code shows, then send to everyone who booked.`,
            );
          } else {
            flow.sendAllDone(
              sendAllSentence(
                res,
                "No one who booked has an email address, so nothing was sent.",
              ),
            );
            void utils.adminListEmailSends.invalidate();
          }
        },
        onError: (err) =>
          flow.problem(friendlyError(err, SEND_OFFLINE_SENTENCE)),
      },
    );
  };

  return (
    <>
      <p className={BODY_TEXT}>
        Each guest gets their own entry pass with a code the door team can scan.
        Best sent a day or two before. Walk-ins don't get one.
      </p>
      <TestThenSend
        flow={flow}
        emailName="Entry pass (QR code)"
        audience={audienceCount > 0 ? people(audienceCount) : null}
        noAudienceText="No one who booked has an email address yet, so there's no one to send this to."
        busy={qrPass.isPending}
        onSendTest={() => send("test")}
        onSendAll={() => send("all")}
        alreadySentNote={alreadySentNote}
      />
    </>
  );
}
